import React, { useState } from 'react';

interface LoginModalProps {
  onClose: () => void;
  onLoginSuccess: () => void;
  onLoginError: (error: string) => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({ onClose, onLoginSuccess, onLoginError }) => {
  const [tab, setTab] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const resetForm = () => {
    setUsername('');
    setEmail('');
    setPassword('');
    setErrorMsg('');
    setRegisterSuccess(false);
  };

  const switchTab = (t: 'login' | 'register') => {
    setTab(t);
    resetForm();
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg('');
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        credentials: 'include',
        body: `username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}`,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Login fehlgeschlagen');
      }

      const data = await response.json();
      localStorage.setItem('token', data.access_token);
      onLoginSuccess();
      onClose();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unbekannter Fehler';
      setErrorMsg(msg);
      onLoginError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg('');
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Registrierung fehlgeschlagen');
      }

      setRegisterSuccess(true);
    } catch (error) {
      setErrorMsg(error instanceof Error ? error.message : 'Unbekannter Fehler');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-tabs">
          <button
            className={tab === 'login' ? 'active' : ''}
            onClick={() => switchTab('login')}
            type="button"
          >
            Login
          </button>
          <button
            className={tab === 'register' ? 'active' : ''}
            onClick={() => switchTab('register')}
            type="button"
          >
            Registrieren
          </button>
        </div>

        {tab === 'login' && (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="username">Benutzername</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Passwort</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {errorMsg && <p className="form-error">{errorMsg}</p>}
            <button type="submit" disabled={isLoading}>
              {isLoading ? 'Bitte warten...' : 'Login'}
            </button>
          </form>
        )}

        {tab === 'register' && !registerSuccess && (
          <form onSubmit={handleRegister}>
            <div className="form-group">
              <label htmlFor="reg-username">Benutzername</label>
              <input
                type="text"
                id="reg-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="reg-email">E-Mail</label>
              <input
                type="email"
                id="reg-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="reg-password">Passwort</label>
              <input
                type="password"
                id="reg-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {errorMsg && <p className="form-error">{errorMsg}</p>}
            <button type="submit" disabled={isLoading}>
              {isLoading ? 'Bitte warten...' : 'Registrieren'}
            </button>
          </form>
        )}

        {tab === 'register' && registerSuccess && (
          <div className="register-success">
            <p>Registrierung erfolgreich! Dein Account muss noch vom Admin freigeschaltet werden.</p>
            <button type="button" onClick={() => switchTab('login')}>Zum Login</button>
          </div>
        )}
      </div>
    </div>
  );
};
